import { ReactNode, useCallback, useContext, useState } from 'react'
import { Animation, AnimationStepDetail } from './Animation.tsx'
import { AppActionContext } from './AppState.ts'
import { useEventListener } from './hooks/useEventListener.ts'
import { vec3 } from './VectorMath.ts'

type KeyList = string[]

export function KeyboardHandler(): ReactNode {
  const sendAppAction = useContext(AppActionContext)

  const [keys, setKeys] = useState<KeyList>([])

  useEventListener('keydown', window, keydown => {
    if (keydown.repeat) {
      return
    }
    const keyOffset = offsetForKeys([keydown.code])
    const keyRoll = rollDirectionForKeys([keydown.code])
    if (keyOffset.lengthSquared !== 0 || keyRoll !== 0) {
      setKeys(keys => appendKey(keydown.code, keys))
    } else {
      switch (keydown.code) {
      case 'KeyC':
        sendAppAction({ type: 'lookAtOrigin' })
        break

      case 'KeyR':
        sendAppAction({ type: 'resetSelectedBone' })
        break
      }
    }
  })

  useEventListener('keyup', window, keyup => {
    setKeys(keys => keys.filter(k => k !== keyup.code))
  })

  const offset = offsetForKeys(keys)
  const rollDirection = rollDirectionForKeys(keys)

  const step = useCallback((detail: AnimationStepDetail) => {
    if (offset.lengthSquared !== 0) {
      const speed = speedForElapsedSeconds(detail.elapsed)
      sendAppAction({ type: 'translateCamera', offset: offset.times(detail.dt * speed) })
    }
    if (rollDirection !== 0) {
      sendAppAction({ type: 'rollSelectedBone', dt: rollDirection * detail.dt })
    }
  }, [offset, rollDirection, sendAppAction])

  return keys.length === 0 ? null : <Animation step={step} />
}

function speedForElapsedSeconds(elapsed: number): number {
  return 0.5 + Math.min(Math.max(0, elapsed) * 10, 20)
}

function appendKey(key: string, keys: KeyList): KeyList {
  return keys.filter(k => k !== key).concat([key])
}

function offsetForKeys(keys: KeyList): vec3 {
  let offset = vec3.zero
  if (keys.includes('KeyS')) {
    offset = offset.plus(vec3.zPlus)
  }
  if (keys.includes('KeyA')) {
    offset = offset.plus(vec3.xMinus)
  }
  if (keys.includes('KeyD')) {
    offset = offset.plus(vec3.xPlus)
  }
  if (keys.includes('KeyW')) {
    offset = offset.plus(vec3.zMinus)
  }
  if (keys.includes('KeyQ')) {
    offset = offset.plus(vec3.yPlus)
  }
  if (keys.includes('KeyZ')) {
    offset = offset.plus(vec3.yMinus)
  }
  return offset
}

function rollDirectionForKeys(keys: KeyList): number {
  let direction = 0
  if (keys.includes('ArrowLeft')) {
    direction -= 1
  }
  if (keys.includes('ArrowRight')) {
    direction += 1
  }
  return direction
}
